// ============================================================================
// Numeric coercion helpers.
//
// Blank HTML inputs produce "" which Supabase sends as-is to Postgres. Numeric
// and integer columns reject "" with:
//   invalid input syntax for type numeric: ""
// These helpers normalise blanks to null so the DB column stays nullable and
// explicit 0 is preserved.
// ============================================================================

/**
 * Coerce a value to a `number | null`.
 *
 * Returns `null` for `""`, whitespace-only strings, `undefined`, `null`, and
 * `NaN`. An explicit `0` stays `0`. Everything else is parsed with `Number()`.
 */
export function toNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Like `toNumOrNull` but truncates to an integer (floor towards zero).
 * Useful for count columns (or_count, pr_count, service_days_per_week).
 */
export function toIntOrNull(v: unknown): number | null {
  const n = toNumOrNull(v);
  return n === null ? null : Math.trunc(n);
}
